import { msalInstance } from "../security/authConfig";
import { InteractionRequiredAuthError } from '@azure/msal-browser';
import { apiQueryWithToken } from "../../common/services/apiBaseUrlConfig";
import logger from "../utils/logger";

const escapeODataString = (value) => String(value ?? "").replace(/'/g, "''");

export const getAccount = () => {
  const currentAccounts = msalInstance.getAllAccounts();
  if (currentAccounts === null) {
    logger.info("No accounts detected");
    return null;
  }
  if (currentAccounts.length > 1) {
    logger.info(
      "Multiple accounts detected, need to add choose account code."
    );
    return currentAccounts[0];
  } else if (currentAccounts.length === 1) {
    return currentAccounts[0];
  }
  return null;
}
export const getAccountByEmail = async (email) => {
  const accounts = msalInstance.getAllAccounts();
  const account = accounts[0];

  try {
    const response = await msalInstance.acquireTokenSilent({
      scopes: ["User.ReadBasic.All"],
      account: account,
    });

    const accessToken = response.accessToken;

    //  const responseT = await fetch(
    //       `https://graph.microsoft.com/v1.0/users/${email}?$select=displayName,department,city,country,companyName`,
    //       {
    //         headers: {
    //           Authorization: `Bearer ${accessToken}`,
    //         },
    //       }
    //     );
    //     const abc=await responseT.json();

    const safeEmail = escapeODataString(email);
    const graphResponse = await fetch(`https://graph.microsoft.com/v1.0/users?$filter=(mail eq '${safeEmail}')`, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    });

    const userResult = await graphResponse.json();
    const user = userResult.value[0];

    if (!user) {
      throw new Error("User not found");
    }


    const photoResponse = await fetch(`https://graph.microsoft.com/v1.0/users/${user.id}/photo/$value`, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    });

    if (!photoResponse.ok) {
      throw new Error("Unable to fetch profile photo");
    }

    const blob = await photoResponse.blob();
    const photoUrl = URL.createObjectURL(blob);

    return {
      user,
      photoUrl,
    };

  } catch (error) {
    logger.error(error);
    if (error instanceof InteractionRequiredAuthError) {
      // Handle interactive token request fallback if needed
    }
  }
};

// Cached across the session so repeated chatbot telemetry calls don't each
// trigger a fresh Graph round-trip for the signed-in user's own profile.
let currentUserProfileCache = null;

const EMPTY_PROFILE = { jobTitle: "", country: "", city: "" };

// Returns the signed-in user's own { jobTitle, country, city } via Microsoft
// Graph's /me endpoint, reusing the User.ReadBasic.All scope already consented
// elsewhere in the app. Best-effort: never throws, falls back to empty strings.
export const getCurrentUserProfile = async () => {
  if (currentUserProfileCache) return currentUserProfileCache;

  const accounts = msalInstance.getAllAccounts();
  const account = accounts?.[0];
  if (!account) return EMPTY_PROFILE;

  try {
    const response = await msalInstance.acquireTokenSilent({
      scopes: ["User.ReadBasic.All"],
      account,
    });

    const accessToken = response.accessToken;
    const graphResponse = await fetch(`https://graph.microsoft.com/v1.0/me?$select=jobTitle,country,city`, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    });

    if (!graphResponse.ok) {
      throw new Error("Unable to fetch current user profile");
    }

    const user = await graphResponse.json();
    currentUserProfileCache = {
      jobTitle: user?.jobTitle || "",
      country: user?.country || "",
      city: user?.city || "",
    };
    return currentUserProfileCache;
  } catch (error) {
    logger.error("Fetching current user profile failed:", error);
    return EMPTY_PROFILE;
  }
};

export const getUserPhoto = async (userId) => {
  const accounts = msalInstance.getAllAccounts();
  const account = accounts[0];

  const response = await msalInstance.acquireTokenSilent({
    scopes: ["User.ReadBasic.All"],
    account: account,
  });

  const accessToken = response.accessToken;
  try {
    const photoResponse = await fetch(`https://graph.microsoft.com/v1.0/users/${userId}/photo/$value`, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    });

    if (!photoResponse.ok) {
      throw new Error("Unable to fetch profile photo");
    }

    const blob = await photoResponse.blob();
    const photoUrl = URL.createObjectURL(blob);
    return photoUrl;
  } catch (err) {

    return '';
  }
};


export const searchGraphUsers = async (query) => {
  const accounts = msalInstance.getAllAccounts();
  const account = accounts[0];
  if (!query) return [];
  const response = await msalInstance.acquireTokenSilent({
    scopes: ["User.ReadBasic.All"],
    account: account,
  });

  const accessToken = response.accessToken;

  const safeQuery = escapeODataString(query);
  const endpoint = `https://graph.microsoft.com/v1.0/users?$filter=startswith(displayName,'${safeQuery}') or startswith(mail,'${safeQuery}')&$select=id,displayName,mail,country,jobTitle,city`;

  try {
    const graphResponse = await fetch(endpoint, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    });

    const userResult = await graphResponse.json();

    const users = await Promise.all(
      userResult.value
        .map(async (userData) => {
          const email = userData.mail || userData.userPrincipalName || '';
          if (!email.toLowerCase().endsWith('ey.com')) return null;

          const photoUrl = await getUserPhoto(userData.id);

          return {
            displayName: userData.displayName,
            status: `${email}`,
            image: photoUrl?photoUrl:null,
            variant:'image',
            rank:userData.jobTitle || 'User',
            country: userData.country || '',
            city: userData.city || '',
          
           

          };
        })
    );
    
    return users.filter(Boolean);


  } catch (error) {
    logger.error('Graph API user search error:', error);
    return [];
  }
};


export const handleLogout = () => {
  const account = getAccount();

  const logOutRequest = {
    account,
    postLogoutRedirectUri: "/logout",
  };
  apiQueryWithToken
    .post(`/User/LogoutUser`, {
      emailId: account?.username,
    })
    .then((data) => {
      msalInstance
        .logoutRedirect(logOutRequest)
        .then((data) => {
          sessionStorage.clear();
        })
        .catch((error) => {
          logger.error("Something went wrong - ", error);
        });
    })
    .catch((error) => {
      logger.error("Something went wrong - ", error);
    });
};

export const refreshToken = async () => {
  const account = getAccount();
  if (!account) {
    throw new Error("SessionExpired");
  }

  const silentRequest = {
    scopes: ["openid"],
    account,
    forceRefresh:true
  };

  try {
    const response = await msalInstance.acquireTokenSilent(silentRequest);
    return response.idToken;
  } catch (error) {
    if (error instanceof InteractionRequiredAuthError) {
      throw new Error("SessionExpired");
    } else {
      throw error;
    }
  }
};